import { Helmet } from 'react-helmet-async';
import { encodeArticleForUrl } from '../helpers/helpers';

const SITE_URL = 'https://tsatsky.com';
const DEFAULT_IMAGE = `${SITE_URL}/og-image.jpg`;
const SITE_NAME = 'Цацки';
const SITE_NAME_EN = 'TSATSKY';

const translations = {
    uk: {
        locale: 'uk_UA',
        defaultSubtitle: 'Ювелірний бренд Харківської ювелірної фабрики',
        defaultDescription: 'Елегантні ювелірні вироби від бренду «Цацки» у Харкові. Унікальні колекції каблучок, сережок, підвісок та браслетів ручної роботи від Харківської ювелірної фабрики.',
        organizationDescription: 'Ювелірний бренд Харківської ювелірної фабрики. Унікальні колекції прикрас ручної роботи — каблучки, сережки, підвіски, браслети з золота та срібла.',
        addressLocality: 'Харків',
        addressRegion: 'Харківська область',
        home: 'Головна',
        catalog: 'Каталог',
        collection: 'Колекція',
        article: 'Артикул',
        productDescription: (name) => `${name} від ${SITE_NAME}`,
        catalogTitle: 'Каталог ювелірних виробів',
        catalogDescription: 'Повний каталог ювелірних виробів TSATSKY — золоті та срібні каблучки, сережки, підвіски, браслети.',
    },
    ru: {
        locale: 'ru_RU',
        defaultSubtitle: 'Ювелирный бренд Харьковского ювелирного завода',
        defaultDescription: 'Элегантные ювелирные изделия от бренда «Цацки» в Харькове. Уникальные коллекции колец, серёг, подвесок и браслетов ручной работы от Харьковского ювелирного завода.',
        organizationDescription: 'Ювелирный бренд Харьковского ювелирного завода. Уникальные коллекции украшений ручной работы — кольца, серьги, подвески, браслеты из золота и серебра.',
        addressLocality: 'Харьков',
        addressRegion: 'Харьковская область',
        home: 'Главная',
        catalog: 'Каталог',
        collection: 'Коллекция',
        article: 'Артикул',
        productDescription: (name) => `${name} от ${SITE_NAME}`,
        catalogTitle: 'Каталог ювелирных изделий',
        catalogDescription: 'Полный каталог ювелирных изделий TSATSKY — золотые и серебряные кольца, серьги, подвески, браслеты.',
    },
};

const SEO = ({
    title,
    description,
    image = DEFAULT_IMAGE,
    url,
    type = 'website',
    product = null,
    collection = null,
    productList = null,
    breadcrumbs = null,
    faq = null,
    noindex = false,
    lang = 'uk',
}) => {
    const t = translations[lang] || translations.uk;

    const fullTitle = title ? `${title} | ${SITE_NAME}` : `${SITE_NAME} - ${t.defaultSubtitle}`;
    const fullUrl = url ? `${SITE_URL}${url}` : SITE_URL;
    const metaDescription = description || t.defaultDescription;

    // Enhanced Organization/LocalBusiness Schema
    const organizationSchema = {
        '@context': 'https://schema.org',
        '@type': ['JewelryStore', 'LocalBusiness'],
        '@id': `${SITE_URL}/#organization`,
        name: SITE_NAME,
        alternateName: SITE_NAME_EN,
        url: SITE_URL,
        logo: {
            '@type': 'ImageObject',
            url: `${SITE_URL}/logo512.png`,
            width: 512,
            height: 512,
        },
        image: DEFAULT_IMAGE,
        description: t.organizationDescription,
        address: {
            '@type': 'PostalAddress',
            addressLocality: t.addressLocality,
            addressRegion: t.addressRegion,
            addressCountry: 'UA',
            postalCode: '61000',
        },
        geo: {
            '@type': 'GeoCoordinates',
            latitude: 49.9935,
            longitude: 36.2304,
        },
        sameAs: [
            'https://www.instagram.com/tsatsky.ua/',
            'https://t.me/tsatsky_ua',
            'https://www.pinterest.com/tsatsky_ua/',
        ],
        priceRange: '$$',
        currenciesAccepted: 'UAH',
        paymentAccepted: 'Cash, Credit Card',
        areaServed: {
            '@type': 'Country',
            name: 'Ukraine',
        },
        knowsLanguage: ['uk', 'ru'],
        hasOfferCatalog: {
            '@type': 'OfferCatalog',
            name: t.catalogTitle,
            itemListElement: [
                { '@type': 'OfferCatalog', name: 'Air Gold' },
                { '@type': 'OfferCatalog', name: 'Kolorovi' },
                { '@type': 'OfferCatalog', name: 'Harmony' },
                { '@type': 'OfferCatalog', name: 'Reflect' },
                { '@type': 'OfferCatalog', name: 'Vidrodzhennya' },
                { '@type': 'OfferCatalog', name: 'Billion' },
                { '@type': 'OfferCatalog', name: 'Diamonds' },
            ],
        },
    };

    // WebSite Schema for sitelinks search box
    const websiteSchema = {
        '@context': 'https://schema.org',
        '@type': 'WebSite',
        '@id': `${SITE_URL}/#website`,
        name: SITE_NAME,
        alternateName: SITE_NAME_EN,
        url: SITE_URL,
        inLanguage: lang === 'uk' ? 'uk-UA' : 'ru-RU',
        publisher: {
            '@id': `${SITE_URL}/#organization`,
        },
    };

    // Enhanced Product Schema
    const productSchema = product ? {
        '@context': 'https://schema.org',
        '@type': 'Product',
        '@id': `${fullUrl}/#product`,
        name: product.name,
        description: product.description || t.productDescription(product.name),
        image: Array.isArray(product.image) ? product.image : [product.image],
        sku: product.article,
        mpn: product.article,
        brand: {
            '@type': 'Brand',
            name: SITE_NAME,
            logo: `${SITE_URL}/logo512.png`,
        },
        manufacturer: {
            '@type': 'Organization',
            name: 'Харківська ювелірна фабрика',
            alternateName: 'Kharkiv Jewelry Factory',
        },
        offers: {
            '@type': 'Offer',
            '@id': `${fullUrl}/#offer`,
            price: product.price,
            priceCurrency: 'UAH',
            availability: 'https://schema.org/InStock',
            itemCondition: 'https://schema.org/NewCondition',
            url: fullUrl,
            priceValidUntil: new Date(Date.now() + 30 * 24 * 60 * 60 * 1000).toISOString().split('T')[0],
            seller: {
                '@id': `${SITE_URL}/#organization`,
            },
            shippingDetails: {
                '@type': 'OfferShippingDetails',
                shippingDestination: {
                    '@type': 'DefinedRegion',
                    addressCountry: 'UA',
                },
            },
        },
        ...(product.materialType && { material: product.materialType }),
        ...(product.collection && { category: product.collection }),
        ...(product.productType && { additionalType: product.productType }),
        isRelatedTo: {
            '@type': 'OfferCatalog',
            name: product.collection || t.catalog,
            url: product.collection ? `${SITE_URL}/${product.collection.toLowerCase()}` : `${SITE_URL}/catalog`,
        },
    } : null;

    // Product List Schema for catalog pages
    const productListSchema = productList && productList.length > 0 ? {
        '@context': 'https://schema.org',
        '@type': 'ItemList',
        name: t.catalogTitle,
        description: t.catalogDescription,
        numberOfItems: productList.length,
        itemListElement: productList.slice(0, 20).map((item, index) => ({
            '@type': 'ListItem',
            position: index + 1,
            item: {
                '@type': 'Product',
                name: item.name,
                image: item.image,
                url: `${SITE_URL}/catalog/${item.collection?.toLowerCase() || 'product'}/${encodeArticleForUrl(item.article)}`,
                offers: {
                    '@type': 'Offer',
                    price: item.price,
                    priceCurrency: 'UAH',
                    availability: 'https://schema.org/InStock',
                },
            },
        })),
    } : null;

    // FAQ Schema
    const faqSchema = faq && faq.length > 0 ? {
        '@context': 'https://schema.org',
        '@type': 'FAQPage',
        mainEntity: faq.map(item => ({
            '@type': 'Question',
            name: item.question,
            acceptedAnswer: {
                '@type': 'Answer',
                text: item.answer,
            },
        })),
    } : null;

    // Enhanced Collection Schema
    const collectionSchema = collection ? {
        '@context': 'https://schema.org',
        '@type': 'CollectionPage',
        '@id': `${fullUrl}/#collection`,
        name: `${t.collection} ${collection.name}`,
        description: collection.description,
        url: fullUrl,
        inLanguage: lang === 'uk' ? 'uk-UA' : 'ru-RU',
        isPartOf: {
            '@id': `${SITE_URL}/#website`,
        },
        about: {
            '@type': 'Thing',
            name: collection.name,
            description: collection.description,
        },
        mainEntity: {
            '@type': 'ItemList',
            name: `${t.collection} ${collection.name}`,
            numberOfItems: collection.itemCount || 0,
        },
    } : null;

    // Enhanced Breadcrumb Schema - now supports custom breadcrumbs
    const breadcrumbSchema = (breadcrumbs || url) && url !== '/' ? {
        '@context': 'https://schema.org',
        '@type': 'BreadcrumbList',
        itemListElement: breadcrumbs ? breadcrumbs.map((crumb, index) => ({
            '@type': 'ListItem',
            position: index + 1,
            name: crumb.name,
            item: crumb.url.startsWith('http') ? crumb.url : `${SITE_URL}${crumb.url}`,
        })) : [
            {
                '@type': 'ListItem',
                position: 1,
                name: t.home,
                item: SITE_URL,
            },
            ...(url.startsWith('/catalog') && !product ? [{
                '@type': 'ListItem',
                position: 2,
                name: t.catalog,
                item: `${SITE_URL}/catalog`,
            }] : []),
            ...(product ? [
                {
                    '@type': 'ListItem',
                    position: 2,
                    name: t.catalog,
                    item: `${SITE_URL}/catalog`,
                },
                ...(product.collection ? [{
                    '@type': 'ListItem',
                    position: 3,
                    name: product.collection,
                    item: `${SITE_URL}/${product.collection.toLowerCase()}`,
                }] : []),
                {
                    '@type': 'ListItem',
                    position: product.collection ? 4 : 3,
                    name: product.name || `${t.article} ${product.article}`,
                    item: fullUrl,
                },
            ] : []),
            ...(collection ? [{
                '@type': 'ListItem',
                position: 2,
                name: `${t.collection} ${collection.name}`,
                item: fullUrl,
            }] : []),
        ],
    } : null;

    return (
        <Helmet>
            <html lang={lang} />
            <title>{fullTitle}</title>
            <meta name="description" content={metaDescription} />
            <link rel="canonical" href={fullUrl} />

            {/* Hreflang tags for multi-language SEO */}
            <link rel="alternate" hrefLang="uk" href={fullUrl} />
            <link rel="alternate" hrefLang="x-default" href={fullUrl} />

            {noindex && <meta name="robots" content="noindex, nofollow" />}

            {/* Additional SEO meta tags */}
            <meta name="geo.region" content="UA-63" />
            <meta name="geo.placename" content="Kharkiv" />
            <meta name="geo.position" content="49.9935;36.2304" />
            <meta name="ICBM" content="49.9935, 36.2304" />

            {/* Open Graph */}
            <meta property="og:title" content={fullTitle} />
            <meta property="og:description" content={metaDescription} />
            <meta property="og:type" content={product ? 'product' : type} />
            <meta property="og:url" content={fullUrl} />
            <meta property="og:image" content={image} />
            <meta property="og:image:width" content="1200" />
            <meta property="og:image:height" content="630" />
            <meta property="og:image:alt" content={fullTitle} />
            <meta property="og:site_name" content={SITE_NAME} />
            <meta property="og:locale" content={t.locale} />

            {/* Twitter Card */}
            <meta name="twitter:card" content="summary_large_image" />
            <meta name="twitter:title" content={fullTitle} />
            <meta name="twitter:description" content={metaDescription} />
            <meta name="twitter:image" content={image} />
            <meta name="twitter:image:alt" content={fullTitle} />

            {/* Product-specific OG tags */}
            {product && (
                <>
                    <meta property="product:price:amount" content={product.price} />
                    <meta property="product:price:currency" content="UAH" />
                    <meta property="product:availability" content="in stock" />
                    <meta property="product:brand" content={SITE_NAME} />
                    <meta property="product:condition" content="new" />
                    {product.collection && <meta property="product:category" content={product.collection} />}
                    {product.article && <meta property="product:retailer_item_id" content={product.article} />}
                </>
            )}

            {/* Structured Data - Organization & Website (always present) */}
            <script type="application/ld+json">
                {JSON.stringify(organizationSchema)}
            </script>

            <script type="application/ld+json">
                {JSON.stringify(websiteSchema)}
            </script>

            {/* Structured Data - Product */}
            {productSchema && (
                <script type="application/ld+json">
                    {JSON.stringify(productSchema)}
                </script>
            )}

            {/* Structured Data - Product List (for catalog pages) */}
            {productListSchema && (
                <script type="application/ld+json">
                    {JSON.stringify(productListSchema)}
                </script>
            )}

            {/* Structured Data - Collection */}
            {collectionSchema && (
                <script type="application/ld+json">
                    {JSON.stringify(collectionSchema)}
                </script>
            )}

            {/* Structured Data - Breadcrumbs */}
            {breadcrumbSchema && (
                <script type="application/ld+json">
                    {JSON.stringify(breadcrumbSchema)}
                </script>
            )}

            {/* Structured Data - FAQ */}
            {faqSchema && (
                <script type="application/ld+json">
                    {JSON.stringify(faqSchema)}
                </script>
            )}
        </Helmet>
    );
};

export default SEO;
